import { useCallback, useEffect, useState } from 'react';
import { api, ApiRequestError, type GuestInvite } from '../api.ts';
import { Avatar, Banner, Button, Card } from './ui.tsx';

export function timeLeft(expiresAt: number): string {
  const minutes = Math.max(0, Math.round((expiresAt - Date.now()) / 60_000));
  if (minutes < 60) return `${minutes}m left`;
  return `${Math.round(minutes / 60)}h left`;
}

function InviteRow({
  invite,
  showInviter,
  busy,
  onRevoke,
}: {
  invite: GuestInvite;
  showInviter?: boolean;
  busy: boolean;
  onRevoke: () => void;
}) {
  return (
    <li className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
      {invite.guest ? <Avatar value={invite.guest.avatarValue} /> : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {invite.guest ? (invite.guest.isSetUp ? invite.guest.name : 'Opened') : 'Not opened yet'}
        </p>
        <p className="text-xs text-text-faint">
          {showInviter ? `from ${invite.createdBy.name} · ` : ''}
          {timeLeft(invite.expiresAt)}
        </p>
      </div>
      <Button variant="danger" disabled={busy} onClick={onRevoke}>
        Revoke
      </Button>
    </li>
  );
}

/** A member's own guest links, on the home page. */
export function GuestInvites() {
  const [invites, setInvites] = useState<GuestInvite[] | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setInvites((await api.guestInvites()).invites);
    } catch {
      setInvites([]);
    }
  }, []);

  useEffect(() => void load(), [load]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The link is on screen to copy by hand.
    }
  }

  return (
    <details className="mb-4 rounded-xl border border-border bg-surface">
      <summary className="cursor-pointer px-4 py-3 font-medium">
        Guest links{invites && invites.length > 0 ? ` (${invites.length})` : ''}
      </summary>
      <div className="flex flex-col gap-3 border-t border-border p-4">
        <p className="text-xs text-text-faint">Lets someone without a code play for 24 hours.</p>

        {error && <Banner tone="danger">{error}</Banner>}

        <div>
          <Button
            loading={busy}
            onClick={() =>
              void run(async () => {
                const { token } = await api.createGuestInvite();
                const url = `${window.location.origin}/?guest=${encodeURIComponent(token)}`;
                setLink(url);
                setCopied(false);
                void copy(url);
              })
            }
          >
            New guest link
          </Button>
        </div>

        {link && (
          <div className="flex flex-col gap-2 rounded-lg border border-border-strong bg-bg p-3 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 break-all font-mono text-sm">{link}</code>
            <Button variant="secondary" onClick={() => void copy(link)}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}

        {invites && invites.length > 0 && (
          <ul className="divide-y divide-border">
            {invites.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                busy={busy}
                onRevoke={() => void run(() => api.revokeGuestInvite(invite.id).then(() => undefined))}
              />
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

/** Every live guest link, for the admin page. */
export function AdminGuests() {
  const [invites, setInvites] = useState<GuestInvite[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setInvites((await api.adminGuests()).invites);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load guests.');
    }
  }, []);

  useEffect(() => void load(), [load]);

  async function revoke(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.revokeGuestInvite(id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not revoke that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-6 p-0">
      <details>
        <summary className="cursor-pointer px-4 py-3 font-medium">
          Guests{invites ? ` (${invites.length})` : ''}
        </summary>
        <div className="border-t border-border px-4 py-3">
          {error && <div className="mb-3"><Banner tone="danger">{error}</Banner></div>}
          {invites === null ? (
            <p className="text-sm text-text-faint">Loading…</p>
          ) : invites.length === 0 ? (
            <p className="text-sm text-text-faint">No active guest links.</p>
          ) : (
            <ul className="divide-y divide-border">
              {invites.map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  showInviter
                  busy={busy}
                  onRevoke={() => void revoke(invite.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </details>
    </Card>
  );
}
